import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ProductListPartialViewModel } from 'autogen/swagger/all';
import isClient from 'helpers/isClient';
import { fetchJSON, formatParams } from 'modules/helpers/fetch';
import useUncommittedSearchParams from '../../components/ProductListFilter/shared/hooks/useUncommittedSearchParams';
import useHasSearchParamsChanged from './useHasSearchParamsChanged';

const groupParamsByKey = (params) =>
    [...params.entries()].reduce((acc, tuple) => {
        // getting the key and value from each tuple
        const [key, val] = tuple;
        if (Object.hasOwn(acc, key)) {
            // if the current key is already an array, we'll add the value to it
            if (Array.isArray(acc[key])) {
                acc[key] = [...acc[key], val];
            } else {
                // if it's not an array, but contains a value, we'll convert it into an array
                // and add the current value to it
                acc[key] = [acc[key], val];
            }
        } else {
            // plain assignment if no special case is present
            acc[key] = val;
        }

        return acc;
    }, {});

export default function useGetProductListPartialQuery(
    initialModel: undefined | ProductListPartialViewModel,
    queryParameters,
    enabled = false,
    baseQueryKey = 'productListPartial'
) {
    /*
        The reason we don't use a swagger endpoint is because of the way the routing works in the backend.
        each plp route has a different endpoint baseUrl + baseUrlKeys/filterUrlKeys + searchParams.
        we cannot reflect the urlKeys/filterKeys in swagger(yet).
        An endpoint would look something like this:
        matas.dk/hudpleje/kropspleje?brand=aco.
        where the urlkeys/filterUrlKeys would be the hudpleje/kropspleje part.
    */
    const productListPartialEndpoint = isClient
        ? window.location.href.split(/[?#]/)[0] + formatParams({ ...queryParameters })
        : '/';

    /*
        Every time queryParameters changes we fetch new PLP data.
        the queryParameters are treated as part of the queryKey
        this also simplifies the logic greatly

        note: we don't set the initialModel as initialData in useQuery, because it takes precedence over the keepPreviousData property.
        this would cause the initialData to be shown as placeholder data between each subsequent fetch and we don't want that behavior.
        we want the previousData to be shown as placeholder between each subsequent fetch.
    */

    const query = useQuery({
        enabled,
        queryKey: [baseQueryKey, queryParameters],
        queryFn: () => fetchJSON<ProductListPartialViewModel>(productListPartialEndpoint),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        refetchOnMount: false
    });

    if (!query.data) return { ...query, data: initialModel };

    return query;
}

// initialModel should only be parsed as undefined if query is used to refetch product list data from outside the product list
export function useGetProductListPartial(initialModel?: ProductListPartialViewModel) {
    const [searchParams] = useSearchParams();
    const queryParameters = groupParamsByKey(searchParams);
    const hasSearchParamsChanged = useHasSearchParamsChanged(searchParams);

    return useGetProductListPartialQuery(initialModel, queryParameters, hasSearchParamsChanged);
}

export function useGetProductListPartialForFilterMenu(initialModel: ProductListPartialViewModel) {
    const { uncommittedSearchParams } = useUncommittedSearchParams();
    const queryParameters = groupParamsByKey(uncommittedSearchParams);
    queryParameters.pagesize = '1'; // set page size to reduce amount of data
    const baseQueryKey = 'filterMenuData';
    const hasSearchParamsChanged = useHasSearchParamsChanged(uncommittedSearchParams);

    return useGetProductListPartialQuery(initialModel, queryParameters, hasSearchParamsChanged, baseQueryKey);
}
